import React, { useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  CustomSelect,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HeaderInput,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { ExamLevelChecking } from '../../../api/candidate/exam/model';
import { usePostGroupAmendmentCheckEligibility } from '../../../api-refactored/group-subject-amendment/usePostGroupAmendmentCheckEligibility';
import { useGetExamLevels } from '../../../api/candidate/exam/@coordinator/queries-services/queries';

const useStyles = makeStyles()((theme) =>
({
  customClass: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  errString: {
    color: theme.color.state.stateError,
  },
}));

interface SelectExamLevelModalProps {
  handleProceed: (examLevel: string) => void;
  examRegistrationType: string;
  modalState: boolean;
  onDialogClose: () => void;
}

const SelectExamLevelModal = ({
  handleProceed,
  examRegistrationType,
  modalState,
  onDialogClose,
}: SelectExamLevelModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [examLevel, setExamLevel] = useState<string>();
  const [examLevelChecking, setExamLevelChecking] = useState<ExamLevelChecking>();
  const [error, setError] = useState('');

  const { data: menuItems } = useGetExamLevels();
  const {
    mutate: checkEligibilityMutate,
    status: checkEligibilityStatus,
  } = usePostGroupAmendmentCheckEligibility({
    onSuccess: (res) => {
      if (!res.eligible) {
        if (res.error.code === '1007') {
          setError(t('amendment->>AmendmentRequestListing.NoAccess'));
        } else if (res.error.code === '5004') {
          setError(t('amendment->>AmendmentRequestListing.SubjectAmendmentNotUnavailable'));
        } else if (res.error.code === '5007') {
          setError(t('amendment->>AmendmentRequestListing.NoCandidateRegistered'));
        }
      } else if (examLevel) {
        handleProceed(examLevel);
      }
    },
    onError: (err) => {
      if (err.response?.data.code === '5305') {
        setError(err.response?.data.message || '');
      } else {
        setError(t('amendment->>AmendmentRequestListing.InvalidExamType'));
      }
    },
  });

  const handleSelectChange = (examType: string) => {
    setExamLevel(examType);
    setExamLevelChecking({ examType, examRegistrationType });
    setError('');
  };

  const handleProceedClick = () => {
    if (examLevelChecking) {
      checkEligibilityMutate(examLevelChecking);
    }
  };

  return (
    <Dialog open={modalState} onClose={onDialogClose} maxWidth="md">
      <DialogTitle>{t('amendment->>AmendmentRequestListing.SelectExamLevel')}</DialogTitle>
      <DialogContent>
        {menuItems && (
          <HeaderInput
            label={t('amendment->>AmendmentRequestListing.ExamLevel')}
            input={
              <CustomSelect
                id="input-select-group-amendment-exam-level"
                placeholder="Select Exam Level"
                displayEmpty
                onChange={({ target: { value } }) => handleSelectChange(value as string)}
                menuItems={menuItems}
                value={examLevel}
              />
            }
          />
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Box className={classes.buttonContainer}>
          <Button onClick={onDialogClose} variant="text" id="button-back">
            {t('amendment->>AmendmentRequestListing.Cancel')}
          </Button>
          <ApiButton
            id="button-proceed"
            status={checkEligibilityStatus}
            type="button"
            disabled={!examLevelChecking || !!error}
            onClick={handleProceedClick}
          >
            {t('amendment->>AmendmentRequestListing.Proceed')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SelectExamLevelModal;
